import { ApolloLink, DocumentTransform, HttpLink } from '@apollo/client'
import { sortTopLevelDefinitions } from '@apollo/persisted-query-lists'
import { GetServerSidePropsContext } from 'next'

import config from '@/config/index'
import hashes from '@/gql/client.json'

export const httpLink = new HttpLink({
  uri: config.apiEndpoint,
})

// This is a custom plugin that follows the Apollo APQ spec
// This preserves the query, whereas the Apollo persistedQueryLink does not
// In the near future when we remove the query we have the option to use the Apollo persistedQueryLink
export const persistedQueryLink = new ApolloLink((operation, forward) => {
  const context = operation.getContext()
  const operationName = operation.operationName

  if (!operationName) {
    console.error('Operation name is missing from the GraphQL operation')
    return forward(operation)
  }

  // Check if we have a pre-generated hash for this operation
  if (operationName in hashes) {
    const sha256Hash = hashes[operationName]

    if (!sha256Hash) {
      console.error(
        `Cannot find pre-generated hash for operation: ${operationName}`
      )
      return forward(operation)
    }

    // Add the persisted query extension
    operation.extensions.persistedQuery = {
      version: 1,
      sha256Hash,
    }

    // Ensure both query and extensions are included
    operation.setContext({
      ...context,
      http: {
        ...context.http,
        includeQuery: true,
        includeExtensions: true,
      },
    })
  }

  return forward(operation)
})

// This is used to send the current URL to the server
// The server then maps it to a certain context to decide if permissions should be granted
// e.g. if a user can create a sitediary and they are on a site diary page, they should be able to create attachments too
export const createContextLink = (ctx?: GetServerSidePropsContext) => {
  return new ApolloLink((operation, forward) => {
    let currentUrl: string | null = null

    if (typeof window !== 'undefined') {
      // Client-side
      currentUrl = window.location.href
    } else if (ctx?.req) {
      // Server-side
      const protocol = ctx.req.headers['x-forwarded-proto'] || 'http'
      const host = ctx.req.headers['x-forwarded-host'] || ctx.req.headers.host
      const url = ctx.req.url
      currentUrl = `${protocol}://${host}${url}`
    }

    operation.setContext(({ headers = {} }) => ({
      headers: {
        ...headers,
        'X-Current-URL': currentUrl,
      },
    }))

    return forward(operation)
  })
}

// Ensure deterministic ordering of operations to match the manifest
export const manifestStyleSortTransform = new DocumentTransform((document) => {
  return sortTopLevelDefinitions(document)
})

// Update the combinedPersistedLink to accept a context
export const createCombinedPersistedLink = (
  ctx?: GetServerSidePropsContext
) => {
  return ApolloLink.from([createContextLink(ctx), persistedQueryLink, httpLink])
}
